import { useEffect, useState } from "react";
import shortid from "shortid";
import {
  Button,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  Box,
  Grid,
  TextField,
  CurrencyInput,
  Paper,
  DataGrid,
  Loading,
} from "../../../../components";
import { formatPrice, responseErros, unformatPrice } from "../../../../utils";
import {
  terceirizadoListValidator,
  terceirizadoValidator,
} from "../middlewares/terceirizado-servico.validator";
import { InputErros } from "../../../../helpers";
import { TerceirizadosService, notification } from "../../../../services";

export function TerceirizadoServicoDialog({ open, setOpen }) {
  const [terceirizados, setTerceirizados] = useState([]);
  const [terceirizado, setTerceirizado] = useState({});
  const [isItemEdit, setIsItemEdit] = useState(false);
  const [idsRemoved, setIdsRemoved] = useState([]);
  const [loading, setLoading] = useState(false);
  const terceirizadoService = new TerceirizadosService();

  const [inputErro, setInputErro] = useState([]);
  const inputErros = new InputErros(inputErro, setInputErro);

  useEffect(() => {
    if (open) {
      buscarTerceirizados();
    }
  }, [open]);

  async function buscarTerceirizados() {
    setLoading(true);
    const result = await terceirizadoService.getAll();
    if (!result.isAxiosError) {
      setTerceirizados(result.data.rows);
    } else {
      responseErros(result);
    }
    setLoading(false);
  }

  const columns = [
    { field: "descricao", headerName: "Descrição", flex: 300 },
    {
      field: "valorServico",
      headerName: "Valor Serviço",
      flex: 80,
      valueFormatter: (params) => `R$ ${formatPrice(params.value || 0, false)}`,
    },
  ];

  function onClose() {
    setTerceirizado({});
    setTerceirizados([]);
    setIdsRemoved([]);
    setIsItemEdit(false);
    setOpen(false);
  }

  function adicionar(item = terceirizado) {
    const terceirizadoTemp = {
      id: item?.itemEditado ? item.id : shortid.generate(),
      descricao: item.descricao,
      valorServico: parseFloat(item.valorServico) || 0,
    };

    terceirizadoValidator
      .validate(terceirizadoTemp, { abortEarly: false })
      .then(() => {
        if (item?.itemEditado) {
          setTerceirizados((prev) =>
            prev.map((m) => (m.id === item.id ? terceirizadoTemp : m)),
          );
          setIsItemEdit(true);
        } else {
          setTerceirizados((prev) => [terceirizadoTemp, ...prev]);
        }
        setTerceirizado({});
        inputErros.limpar();
      })
      .catch((err) => {
        inputErros.set(err);
      });
  }

  async function upsertTerceirizados() {
    terceirizadoListValidator
      .validate(terceirizados, { abortEarly: false })
      .then(async () => {
        setLoading(true);
        const body = { items: terceirizados, idsRemoved };
        const result = await terceirizadoService.upsert(body);
        if (!result.isAxiosError) {
          notification.alteracaoSucesso();
          onClose();
        } else {
          responseErros(result);
        }
        setLoading(false);
      })
      .catch((err) => {
        inputErros.set(err);
      });
  }

  function onClickEditTerceirizado(id) {
    const edit = terceirizados.find((m) => m.id === id);
    if (edit) {
      setTerceirizado({ ...edit, itemEditado: true });
    }
  }

  function onClickDeleteTerceirizado(id) {
    const removed = terceirizados.find((m) => m.id === id);
    if (removed && typeof removed.id === "number") {
      setIdsRemoved((prev) => [...prev, removed.id]);
    }
    setTerceirizados((prev) => prev.filter((m) => m.id !== id));
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperComponent="div"
      PaperProps={{ style: { borderRadius: "0.7rem", background: "#ffffff" } }}
    >
      <DialogContent>
        <CardHeader title="Novo Terceirizado" sx={{ padding: "1rem 1.6rem" }} />

        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8}>
              <TextField
                id="descricao"
                name="descricao"
                label="Descrição"
                required
                value={terceirizado.descricao || ""}
                onChange={(e) =>
                  setTerceirizado({
                    ...terceirizado,
                    descricao: e.target.value,
                  })
                }
                fullWidth
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <CurrencyInput
                label="Valor Serviço"
                value={terceirizado.valorServico || 0}
                onChange={(e) =>
                  setTerceirizado({
                    ...terceirizado,
                    valorServico: unformatPrice(e.target.value),
                  })
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                fullWidth
                onClick={() => adicionar(terceirizado)}
              >
                {terceirizado.itemEditado ? "Atualizar" : "Adicionar"}
              </Button>
            </Grid>
          </Grid>
        </Paper>
        <Box>
          <DataGrid
            rows={terceirizados}
            columns={columns}
            onClickEdit={onClickEditTerceirizado}
            onClickDelete={onClickDeleteTerceirizado}
            editShow
            deleteShow
          />
        </Box>
      </DialogContent>
      <DialogActions className="me-2">
        <Button
          onClick={onClose}
          color="padrao"
          variant="contained"
          disableElevation
        >
          Fechar
        </Button>
        {((terceirizados.length > 0 &&
          terceirizados.some((m) => typeof m.id === "string")) ||
          isItemEdit ||
          idsRemoved?.length > 0) && (
          <Button
            onClick={upsertTerceirizados}
            color="primary"
            variant="contained"
            disableElevation
          >
            Salvar
          </Button>
        )}
      </DialogActions>
      <Loading loading={loading} />
    </Dialog>
  );
}
